import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BiLoaderCircle } from "react-icons/bi";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { CloudUpload } from "@mui/icons-material";
import { IoVolumeMute, IoVolumeHigh } from "react-icons/io5";
import { FaPlay, FaPause } from "react-icons/fa";
import { IoMdClose } from "react-icons/io"; // Import close icon
import { useSelector } from "react-redux";
import { Box, useMediaQuery } from "@mui/material";
import { Client, Storage, ID, Databases } from "appwrite";
import { 
  createPostUploadNotificationForFollowersSafe 
} from './appwriteFollowService'; // Adjust the import path as needed

import logo from './img/logo.png';

// Initialize Appwrite with environment variables
const client = new Client();
client
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

const storage = new Storage(client);
const databases = new Databases(client);

// Database and collection values
const DATABASE_ID = "681f7caf0033c79e0bc0";
const COLLECTION_ID = "681f80c000210a12ef65";
const BUCKET_ID = process.env.REACT_APP_APPWRITE_BUCKET_ID;

export default function Upload() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");
  const isTabletScreen = useMediaQuery("(min-width: 600px) and (max-width: 999px)");
  const isMobileScreen = useMediaQuery("(max-width: 599px)");

  const [user, setUser] = useState(null);
  const _id = user?._id; // Avoids destructuring null

  const token = useSelector((state) => state.token);

  let [fileDisplay, setFileDisplay] = useState("");
  let [caption, setCaption] = useState("");
  let [file, setFile] = useState(null);
  let [error, setError] = useState(null);
  let [isUploading, setIsUploading] = useState(false);
  let [selectedTalent, setSelectedTalent] = useState("");
  const [customTalent, setCustomTalent] = useState("");
  const [isMuted, setIsMuted] = useState(true);
  
  // New state variables for video player functionality
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  const talents = ["Singing", "Rapping", "Dancing", "Acting", "Magic", "Comedy", "Art", "Instrumental", "Photography", "Other"];

  // Function to fetch user details
  const getUserDetails = async (userId) => {
    try {
      const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${userId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error("Failed to fetch user details");
      return await response.json();
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  };

  // Function to fetch user details
  const getUser = async () => {
    try {
      const response = await fetch(`https://my-pers-thing-server.onrender.com/users/${userId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();
      setUser(data);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  useEffect(() => {
    if (userId) {
      getUser();
    }
  }, [userId]); // Fetch user data when userId changes

  // Add this to useEffect to manage video playback when component mounts/unmounts
useEffect(() => {
  // Set up video autoplay when file is displayed
  if (fileDisplay && videoRef.current) {
    videoRef.current.play().catch(e => {
      // Autoplay was prevented, set isPlaying to false
      setIsPlaying(false);
      console.log("Autoplay prevented:", e);
    });
  }
  
  // Clean up function
  return () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };
}, [fileDisplay]);

  // Video control functions
  const togglePlay = (e) => {
    if (e) e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

// Video Progress Bar - Making it more touch-friendly on mobile
const handleProgressBarClick = (e) => {
  if (videoRef.current) {
    e.stopPropagation(); // Prevent event bubbling
    const progressBar = e.currentTarget;
    const clickPosition = (e.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth;
    const newTime = clickPosition * videoRef.current.duration;
    videoRef.current.currentTime = newTime;
    setProgress(clickPosition * 100);
  }
};

// Touch event handler for mobile progress bar
const handleProgressBarTouch = (e) => {
  if (videoRef.current) {
    e.stopPropagation(); // Prevent event bubbling
    const progressBar = e.currentTarget;
    const touch = e.touches[0];
    const touchPosition = (touch.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth;
    const newTime = touchPosition * videoRef.current.duration;
    videoRef.current.currentTime = newTime;
    setProgress(touchPosition * 100);
  }
};

  const onChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      const fileUrl = URL.createObjectURL(selectedFile);
      setFileDisplay(fileUrl);
      setFile(selectedFile);
      // Reset video-related states when new video is selected
      setProgress(0);
      setIsPlaying(true);
    }
  };

  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    setIsMuted(!isMuted);
  };

  const discard = () => {
    setFileDisplay("");
    setFile(null);
    setCaption("");
    setSelectedTalent("");
    setCustomTalent("");
    setProgress(0);
    setIsPlaying(true);
  };

  const clearVideo = () => {
    setFileDisplay("");
    setFile(null);
    setProgress(0);
    setIsPlaying(true);
  };

  const validate = () => {
    setError(null);
    let isError = false;

    if (!file) {
      setError({ type: "File", message: "A video is required" });
      isError = true;
    } else if (!caption) {
      setError({ type: "caption", message: "A note is required" });
      isError = true;
    }
    else if (!selectedTalent || (selectedTalent === "Other" && !customTalent.trim())) {
      setError({ type: "talent", message: "Please choose your talent" });
      isError = true;
    }
    return isError;
  };

  const createNewPost = async () => {
    let isError = validate();
    if (isError) return;
    setIsUploading(true);
  
    try {
      // Get user details first
      const userDetails = await getUserDetails(_id);
      if (!userDetails) {
        throw new Error("Could not retrieve user details");
      }
  
      // Upload the video file to Appwrite Storage
      const uploadResponse = await storage.createFile(
        BUCKET_ID,
        ID.unique(),
        file
      );
  
      console.log("Video uploaded successfully! File ID:", uploadResponse.$id);
      
      // Create the document in the database
      const talentValue = selectedTalent === "Other" ? customTalent : selectedTalent;
      
      const postId = ID.unique();
      const newPost = await databases.createDocument(
        DATABASE_ID,
        COLLECTION_ID,
        postId, // Use this ID directly
        {
          userId: _id,
          fileId: uploadResponse.$id,
          description: caption,
          talent: talentValue,
          createdAt: new Date().toISOString(),
          userName: userDetails?.firstName + " " + userDetails?.lastName || "User",
          userImage: userDetails?.picturePath || "" // Use empty string instead of null
        }
      );
      
      console.log("Post document created in database with ID:", postId);
  
      // After successful post creation, notify followers
      const userName = `${userDetails.firstName} ${userDetails.lastName}`;
      try {
        await createPostUploadNotificationForFollowersSafe(
          _id, // Uploader ID
          userName, // Uploader name
          userDetails.picturePath, // Profile picture
          postId // Post ID for redirection
        );
        console.log("Post upload notifications sent to followers");
      } catch (notifError) {
        console.log("Failed to send post upload notifications:", notifError);
        // Don't block the main flow if notifications fail
      }
  
      setIsUploading(false);
      // Navigate to home page after successful upload
      navigate(`/home`);
  
    } catch (error) {
      console.error("Error with Appwrite:", error);
      setIsUploading(false);
      setError({ 
        type: "upload", 
        message: error.message || "Error uploading video" 
      });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        padding: isMobileScreen ? "16px" : "20px",
        backgroundColor: "#ffffff", // Changed to white background
      }}
    >
      {/* Logo positioned with responsive positioning */}
      <div style={{
        position: "absolute",
        top: isMobileScreen ? "16px" : "30px",
        left: isMobileScreen ? "16px" : "60px",
      }}>
        <Box
          component="img"
          src={logo}
          alt="Company Logo"
          sx={{
            height: isMobileScreen ? "40px" : "50px",
          }}
        />
      </div>

      {/* Main Content Area */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          flex: 1,
          padding: isMobileScreen ? "40px 0" : "20px",
          marginTop: isMobileScreen ? "50px" : "0",
        }}
      >
        {/* Video Upload Section */}
        <div
          style={{
            display: "flex",
            flexDirection: isNonMobileScreens ? "row" : "column",
            alignItems: isNonMobileScreens ? "flex-start" : "center",
            gap: isNonMobileScreens ? "100px" : "40px",
            width: "100%",
            maxWidth: isNonMobileScreens ? "1000px" : "100%",
            backgroundColor: "white",
            borderRadius: "10px",
            padding: isMobileScreen ? "16px" : isTabletScreen ? "20px" : "24px",
            boxSizing: "border-box",
            // boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {/* Video Selection Box - Enhanced to match Post style */}
          <div
  style={{
    width: isNonMobileScreens ? "380px" : isTabletScreen ? "380px" : "100%",
    height: isNonMobileScreens ? "500px" : isTabletScreen ? "480px" : "400px", // Reduced height on mobile
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "0",
    border: fileDisplay ? "none" : "3px double #ccc",
    borderRadius: "20px",
    cursor: "pointer",
    position: "relative",
    boxSizing: "border-box",
    background: fileDisplay ? "#000" : "white",
    overflow: "hidden",
    margin: isMobileScreen ? "0 auto" : "0", // Center on mobile
    maxWidth: isMobileScreen ? "95%" : "100%", // Prevent overflow on small screens
  }}
>
            {!fileDisplay ? (
              <label
                htmlFor="fileInput"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files.length > 0) {
                    const droppedFile = e.dataTransfer.files[0];
                    const fileUrl = URL.createObjectURL(droppedFile);
                    setFileDisplay(fileUrl);
                    setFile(droppedFile);
                  }
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <CloudUpload style={{ fontSize: isMobileScreen ? "30px" : "40px", color: "#b3b3b1" }} />
                <p style={{ marginTop: "16px", fontSize: isMobileScreen ? "15px" : "17px" }}>Select video to upload</p>
                <p style={{ marginTop: "-10px", color: "gray", fontSize: isMobileScreen ? "12px" : "13px" }}>Or drag and drop</p>
                <p style={{ 
                  marginTop: isMobileScreen ? "20px" : "48px", 
                  color: "gray", 
                  fontSize: isMobileScreen ? "12px" : "14px",
                }}>Less than 5GB</p>
                <label
                  htmlFor="fileInput"
                  style={{
                    marginTop: "16px",
                    padding: "10px",
                    width: "60%",
                    backgroundColor: "#4681f4",
                    color: "white",
                    borderRadius: "50px",
                    textAlign: "center",
                    cursor: "pointer",
                    fontSize: isMobileScreen ? "14px" : "16px",
                  }}
                >
                  Select file
                </label>
                <input type="file" id="fileInput" onChange={onChange} hidden accept="video/*" maxSize={5368709120}
 />
              </label>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  borderRadius: "20px",
                  overflow: "hidden",
                }}
              >
                {/* Close Icon - Added for discarding video */}
                <div
                  onClick={clearVideo}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    width: isMobileScreen ? "28px" : "32px",
                    height: isMobileScreen ? "28px" : "32px",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 25,
                    transition: "transform 0.2s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <IoMdClose size={isMobileScreen ? 18 : 20} color="#fff" />
                </div>

                {isUploading && (
                  <div
                    style={{
                      position: "absolute",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: "20",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      height: "100%",
                      width: "100%",
                      borderRadius: "20px",
                    }}
                  >
                    <BiLoaderCircle className="animate-spin" color="#F12B56" size={isMobileScreen ? 24 : 30} />
                    <div style={{ color: "white", fontWeight: "bold", marginTop: "8px" }}>Uploading...</div>
                  </div>
                )}
                <video
                  id="preview-video"
                  ref={videoRef}
                  autoPlay
                  loop
                  muted={isMuted}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "20px",
                    objectFit: "contain",
                  }}
                  src={fileDisplay}
                  onTimeUpdate={handleTimeUpdate}
                />
                
                {/* "Tap for sound" overlay */}
                {isMuted && (
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      left: "8px",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      borderRadius: "8px",
                      padding: "4px 10px",
                      color: "white",
                      fontSize: isMobileScreen ? "11px" : "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      zIndex: 10,
                      transition: "all 0.2s ease",
                      animation: "pulse 2s infinite"
                    }}
                    onClick={(e) => toggleMute(e)}
                  >
                    <IoVolumeMute size={16} color="#fff" />
                    Tap for sound
                  </div>
                )}
                
                {/* Media controls */}
                <div
  style={{
    position: "absolute",
    bottom: "10px",
    left: "10px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    zIndex: 10,
  }}
>
  {/* Mute Button - Enlarged for mobile */}
  <div
    onClick={(e) => toggleMute(e)}
    style={{
      width: isMobileScreen ? "32px" : "32px",
      height: isMobileScreen ? "32px" : "32px",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {isMuted ? 
      <IoVolumeMute size={isMobileScreen ? 20 : 18} color="#fff" /> :
      <IoVolumeHigh size={isMobileScreen ? 20 : 18} color="#fff" />
    }
  </div>
                  
                  {/* Play/Pause Button */}
                  <div
    onClick={togglePlay}
    style={{
      width: isMobileScreen ? "32px" : "32px",
      height: isMobileScreen ? "32px" : "32px",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {isPlaying ? 
      <FaPause size={isMobileScreen ? 16 : 14} color="#fff" /> :
      <FaPlay size={isMobileScreen ? 16 : 14} color="#fff" />
    }
  </div>
</div>
                
                {/* Video Progress Bar */}
<div
  style={{
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: isMobileScreen ? "8px" : "6px", // Increased height on mobile for easier touch
    backgroundColor: "rgba(255,255,255,0.3)",
    zIndex: 5,
    cursor: "pointer",
  }}
  onClick={handleProgressBarClick}
  onTouchStart={handleProgressBarTouch}
>
  <div
    style={{
      height: "100%",
      width: `${progress}%`,
      background: "linear-gradient(to right, #F02C56, #FF6A88)",
      transition: "width 0.1s linear",
    }}
  />
</div>
              </div>
            )}
          </div>

          {/* Right Side: Choose Your Talent, Add Note, and Buttons */}
          <div
            style={{
              flex: "1",
              width: isNonMobileScreens ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: "15px",
              marginTop: isNonMobileScreens ? "0" : "10px",
            }}
          >
            {/* Talent Selection */}
            <div>
              <p style={{ 
                fontSize: isMobileScreen ? "14px" : "15px", 
                fontWeight: "bold", 
                marginBottom: "8px" 
              }}>
                Choose Your Talent
              </p>
              <div style={{ 
  display: "flex", 
  gap: isMobileScreen ? "6px" : "10px", 
  flexWrap: "wrap", 
  justifyContent: isMobileScreen ? "center" : "flex-start"
}}>
  {talents.map((talent, index) => (
    <div
      key={index}
      onClick={() => {
        setSelectedTalent(talent);
        if (talent !== "Other") setCustomTalent("");
      }}
      style={{
        padding: isMobileScreen ? "6px 12px" : "10px 20px",
        borderRadius: "10px",
        cursor: "pointer",
        border: selectedTalent === talent ? "2px solid #ff7f00" : "2px solid #ccc",
        backgroundColor: selectedTalent === talent ? "#ff7f00" : "white",
        color: selectedTalent === talent ? "white" : "black",
        fontWeight: selectedTalent === talent ? "bold" : "normal",
        fontSize: isMobileScreen ? "12px" : "14px",
        marginBottom: "8px",
        textAlign: "center",
        minWidth: isMobileScreen ? "60px" : "auto",
      }}
    >
      {talent}
    </div>
  ))}
</div>

              {/* Custom Talent Input */}
              {selectedTalent === "Other" && (
                <input
                  type="text"
                  placeholder="Enter your talent..."
                  value={customTalent}
                  onChange={(e) => setCustomTalent(e.target.value)}
                  style={{
                    width: "100%",
                    border: "1px solid gray",
                    padding: "10px",
                    borderRadius: "8px",
                    marginTop: "10px",
                    fontSize: isMobileScreen ? "13px" : "14px",
                    boxSizing: "border-box",
                  }}
                />
              )}
            </div>

            {/* Add Note Section */}
            <div>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                fontSize: isMobileScreen ? "14px" : "15px", 
                marginTop: isNonMobileScreens ? "60px" : isTabletScreen ? "40px" : "30px" 
              }}>
                <div>Add a note</div>
                <div style={{ color: "gray", fontSize: isMobileScreen ? "11px" : "12px" }}>{caption.length}/150</div>
              </div>
              <input
                maxLength={150}
                type="text"
                placeholder="Write a note..."
                style={{
                  width: "100%",
                  border: "1px solid gray",
                  padding: "10px",
                  borderRadius: "8px",
                  marginTop: "8px",
                  fontSize: isMobileScreen ? "13px" : "14px",
                  boxSizing: "border-box",
                }}
                value={caption}
                onChange={(event) => setCaption(event.target.value)}
              />
            </div>

            {/* Buttons */}
            <div style={{ 
              display: "flex", 
              gap: "10px",
              flexDirection: isMobileScreen ? "column" : "row",
              marginTop: isMobileScreen ? "10px" : "0",
            }}>
              <button
                onClick={discard}
                style={{
                  flex: "1",
                  padding: isMobileScreen ? "8px 16px" : "10px 24px",
                  border: "1px solid gray",
                  fontSize: isMobileScreen ? "14px" : "16px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  backgroundColor: "#f8f8f8",
                  width: isMobileScreen ? "100%" : "auto",
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNewPost}
                style={{
                  flex: "1",
                  padding: isMobileScreen ? "8px 16px" : "10px 24px",
                  backgroundColor: "#4681f4",
                  color: "white", 
                  fontSize: isMobileScreen ? "14px" : "16px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                  width: isMobileScreen ? "100%" : "auto",
                }}
              >
                {isUploading ? (
                  <BiLoaderCircle className="animate-spin" color="#ffffff" size={isMobileScreen ? 20 : 25} />
                ) : (
                  <>
                    <AiOutlineCheckCircle size={isMobileScreen ? 16 : 20} />
                    Post
                  </>
                )}
              </button>
            </div>

            {/* Error Message */}
            {error && <div style={{ 
  color: "red", 
  marginTop: "12px",
  padding: isMobileScreen ? "8px" : "0",
  textAlign: isMobileScreen ? "center" : "left",
  fontSize: isMobileScreen ? "13px" : "14px", 
  backgroundColor: isMobileScreen ? "rgba(255,0,0,0.05)" : "transparent",
  borderRadius: isMobileScreen ? "5px" : "0",
}}>{error.message}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
